import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { apiClient } from '../../services/api';
import toast from 'react-hot-toast';
import { getImageUrl, hideBrokenImage } from '../../utils/imageUrl';

// Quill rich-text editor configuration for blog content
const quillModules = {
  toolbar: [
    [{ header: [2, 3, 4, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['blockquote', 'code-block'],
    ['link', 'image'],
    [{ color: [] }, { background: [] }],
    ['clean']
  ]
};

const isQuillEmpty = (html) => !html || !html.replace(/<[^>]*>/g, '').trim();

// Pasted content often carries non-breaking spaces, which prevent word
// wrapping and make article text overflow its container — normalize them.
const sanitizeContent = (html) =>
  (html || '').replace(/&nbsp;/g, ' ').replace(/ /g, ' ');

const BlogManagement = () => {
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    category_id: '',
    status: 'draft',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    tags: ''
  });
  const [featuredImage, setFeaturedImage] = useState(null);
  const [existingFeaturedImage, setExistingFeaturedImage] = useState(null);

  useEffect(() => {
    fetchBlogs();
    fetchCategories();
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/blogs?status=draft,published,archived&limit=50');
      setBlogs(response.data.blogs || response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch blogs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiClient.get('/categories');
      setCategories(response.data.categories || response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      console.log('Image selected:', file.name, file.size, file.type);
      setFeaturedImage(file);
    }
  };

  const generateSlug = () => {
    const slug = formData.title
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^\w-]/g, '');
    setFormData(prev => ({ ...prev, slug }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isQuillEmpty(formData.content)) {
      toast.error('Content is required');
      return;
    }
    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'content') {
          data.append('content', sanitizeContent(formData.content));
        } else if (key === 'tags') {
          const cleanTags = (formData.tags || '')
            .split(',')
            .map(t => t.trim())
            .filter(t => t.length >= 2);
          data.append('tags', JSON.stringify(cleanTags));
        } else {
          data.append(key, formData[key]);
        }
      });
      if (featuredImage) {
        console.log('Appending featured image:', featuredImage.name);
        data.append('featured_image', featuredImage);
      }

      const url = editingId ? `/blogs/${editingId}` : '/blogs';
      const method = editingId ? 'PUT' : 'POST';

      console.log('Submitting blog:', { method, url, hasImage: !!featuredImage });

      if (editingId) {
        await apiClient.put(url, data);
        toast.success('Blog updated successfully');
      } else {
        await apiClient.post(url, data);
        toast.success('Blog created successfully');
      }

      resetForm();
      fetchBlogs();
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Failed to save blog';
      toast.error(errorMsg);
      console.error('Blog submission error:', {
        status: error.response?.status,
        message: errorMsg,
        data: error.response?.data
      });
    }
  };

  const handleEdit = async (blog) => {
    setFormData({
      title: blog.title || '',
      slug: blog.slug || '',
      content: blog.content || '',
      excerpt: blog.excerpt || '',
      category_id: blog.category_id || '',
      status: blog.status || 'draft',
      meta_title: blog.meta_title || '',
      meta_description: blog.meta_description || '',
      meta_keywords: blog.meta_keywords || '',
      tags: blog.tags?.map(t => t.name).join(', ') || ''
    });
    setFeaturedImage(null);
    setExistingFeaturedImage(blog.featured_image || null);
    setEditingId(blog.id);
    setShowForm(true);
    // Scroll to top of form
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // If inside a scrollable div (like AdminDashboard), we might need to scroll that instead
    const mainContent = document.querySelector('main');
    if (mainContent) {
      mainContent.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this blog?')) {
      try {
        await apiClient.delete(`/blogs/${id}`);
        toast.success('Blog deleted successfully');
        fetchBlogs();
      } catch (error) {
        toast.error('Failed to delete blog');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      content: '',
      excerpt: '',
      category_id: '',
      status: 'draft',
      meta_title: '',
      meta_description: '',
      meta_keywords: '',
      tags: ''
    });
    setEditingId(null);
    setFeaturedImage(null);
    setExistingFeaturedImage(null);
    setShowForm(false);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Blog Management</h1>
        <button
          onClick={() => {
            setShowForm(!showForm);
            if (!showForm) {
              const mainContent = document.querySelector('main');
              if (mainContent) mainContent.scrollTo({ top: 0, behavior: 'smooth' });
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          {showForm ? 'Cancel' : 'New Blog Post'}
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4">{editingId ? 'Edit Blog' : 'Create New Blog'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title*</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  onBlur={generateSlug}
                  required
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Slug</label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Excerpt</label>
              <textarea
                name="excerpt"
                value={formData.excerpt}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Content*</label>
              <div className="bg-white border rounded-lg overflow-hidden">
                <ReactQuill
                  theme="snow"
                  value={formData.content}
                  onChange={(html) => setFormData(prev => ({ ...prev, content: html }))}
                  modules={quillModules}
                  placeholder="Write your blog content here..."
                  style={{ minHeight: '250px' }}
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">Use the toolbar for headings, formatting, lists, links, and images.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Featured Image (Thumbnail)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
                <p className="text-xs text-gray-400 mt-1">
                  This image is used as the blog thumbnail in lists and previews — images inserted in the content above do not set the thumbnail.
                </p>
                {featuredImage ? (
                  <div className="mt-2 flex items-center gap-3">
                    <img
                      src={URL.createObjectURL(featuredImage)}
                      alt="New thumbnail preview"
                      className="h-16 w-24 object-cover rounded border"
                    />
                    <span className="text-xs text-green-600">New thumbnail: {featuredImage.name}</span>
                  </div>
                ) : existingFeaturedImage ? (
                  <div className="mt-2 flex items-center gap-3">
                    <img
                      src={getImageUrl(existingFeaturedImage)}
                      alt="Current thumbnail"
                      onError={hideBrokenImage}
                      className="h-16 w-24 object-cover rounded border"
                    />
                    <span className="text-xs text-gray-500">Current thumbnail (choose a file to replace)</span>
                  </div>
                ) : (
                  <p className="text-xs text-amber-600 mt-2">No thumbnail set for this post yet.</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                <select
                  name="category_id"
                  value={String(formData.category_id)}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                >
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={String(cat.id)}>{cat.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tags (comma-separated)</label>
                <input
                  type="text"
                  name="tags"
                  value={formData.tags}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Meta Title</label>
              <input
                type="text"
                name="meta_title"
                value={formData.meta_title}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                placeholder="SEO Meta Title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Meta Description</label>
              <textarea
                name="meta_description"
                value={formData.meta_description}
                onChange={handleInputChange}
                rows="2"
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                placeholder="SEO Meta Description"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Meta Keywords</label>
              <input
                type="text"
                name="meta_keywords"
                value={formData.meta_keywords}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                placeholder="keyword1, keyword2, keyword3"
              />
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700"
              >
                {editingId ? 'Update Blog' : 'Create Blog'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-400 text-white px-6 py-2 rounded-lg hover:bg-gray-500"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {blogs.map(blog => {
            const imageUrl = getImageUrl(blog.featured_image);
            return (
            <div key={blog.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition">
              <div className="flex flex-col sm:flex-row items-start gap-4">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={blog.title}
                    className="w-full sm:w-28 h-20 object-cover rounded-lg bg-gray-100 flex-shrink-0"
                    onError={hideBrokenImage}
                  />
                ) : (
                  <div className="w-full sm:w-28 h-20 bg-blue-600 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    FBRSTAPP
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 truncate">{blog.title}</h3>
                  <p className="text-gray-600 text-sm">by {blog.author_name}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Status: <span className={`font-semibold ${blog.status === 'published' ? 'text-green-600' : 'text-yellow-600'}`}>
                      {blog.status}
                    </span>
                    {blog.category_name && <span className="ml-3 text-gray-400">Category: {blog.category_name}</span>}
                  </p>
                </div>
                <div className="flex gap-2 self-end sm:self-center">
                  <button
                    onClick={() => handleEdit(blog)}
                    className="bg-blue-600 text-white px-3 py-1.5 text-xs font-semibold rounded-lg hover:bg-blue-700 transition"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(blog.id)}
                    className="bg-red-600 text-white px-3 py-1.5 text-xs font-semibold rounded-lg hover:bg-red-700 transition"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BlogManagement;
